import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  InputAdornment,
  MenuItem,
  Checkbox,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AutoAwesome as AIIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  BatchPrediction as BatchIcon,
  Build as BuildIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useApi } from '../services/api';
import aiService from '../services/aiService';
import Editor from '@monaco-editor/react';
import TemplateSelector from '../components/TemplateSelector';
import ManualConfigurationForm from '../components/ManualConfigurationForm';

interface Configuration {
  id: string;
  name: string;
  switch_name?: string;
  vendor: string;
  switch_type?: string;
  created_at: string;
  updated_at: string;
  configuration: string;
  generated_config?: string;
  natural_language_input?: string;
  natural_language_request?: string;
  version?: string;
  status?: 'active' | 'archived' | 'draft';
  validation_status?: string;
  ai_generated?: boolean;
  confidence_score?: number;
}

const ConfigurationManagementAI: React.FC = () => {
  const { getAuthenticatedApi } = useApi();
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [filteredConfigs, setFilteredConfigs] = useState<Configuration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [selectedConfigs, setSelectedConfigs] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialog, setViewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [aiDialog, setAiDialog] = useState(false);
  const [batchDialog, setBatchDialog] = useState(false);
  const [manualDialog, setManualDialog] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [configMode, setConfigMode] = useState<'manual' | 'ai'>('manual');
  const [selectedConfig, setSelectedConfig] = useState<Configuration | null>(null);
  const [editableContent, setEditableContent] = useState('');

  // AI Generation states
  const [aiMode] = useState<'single' | 'batch'>('single');
  const [aiDescription, setAiDescription] = useState('');
  const [aiVendor, setAiVendor] = useState('extreme');
  const [aiSwitchType, setAiSwitchType] = useState('edge');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [aiTemplateId, setAiTemplateId] = useState<string | null>(null);
  
  // Manual configuration states
  const [manualVendor, setManualVendor] = useState('extreme');
  const [manualSwitchType, setManualSwitchType] = useState('edge');

  const aiExamples = {
    single: [
      'Configure edge switch SW-FLOOR2-01 at Building A with IP 192.168.1.10 and 48 ports',
      'Set up core switch CORE-01 with VRRP for high availability, primary role, IP 10.220.56.2',
      'Create config for distribution switch with custom VLAN Guest on tag 100 and QoS enabled'
    ],
    batch: [
      'Configure 5 edge switches for Building A floors 1-5, starting IP 192.168.1.10',
      'Set up 3 core switches with VRRP, IPs starting from 10.0.1.1',
      'Create configs for 10 campus edge switches with sequential naming SW-01 to SW-10'
    ]
  };

  useEffect(() => {
    loadConfigurations();
  }, []);

  useEffect(() => {
    filterConfigurations();
  }, [searchTerm, selectedVendor, configurations]);

  const loadConfigurations = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await getAuthenticatedApi();
      const response = await api.listConfigurations();
      
      // The backend returns a direct array, not wrapped in {configurations: [...]}
      const responseArray = Array.isArray(response) ? response : (response?.configurations || []);
      const mappedConfigs = responseArray.map((config: any) => ({
        ...config,
        name: config.switch_name || config.name || 'Unnamed',
        configuration: config.generated_config || config.configuration || '',
        natural_language_request: config.natural_language_input || config.natural_language_request
      }));
      
      setConfigurations(mappedConfigs);
    } catch (error: any) {
      setError(error.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };


  const filterConfigurations = () => {
    let filtered = [...configurations];
    
    if (searchTerm) {
      filtered = filtered.filter(config =>
        config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.natural_language_request?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedVendor !== 'all') {
      filtered = filtered.filter(config => config.vendor === selectedVendor);
    }
    
    setFilteredConfigs(filtered);
  };

  const handleAIGenerate = async () => {
    const validation = aiService.validateDescription(aiDescription);
    if (!validation.isValid) {
      setError(validation.message!);
      return;
    }

    setAiGenerating(true);
    setError(null);
    setAiResult(null);

    try {
      if (aiMode === 'single') {
        const result = await aiService.generateConfiguration({
          description: aiDescription,
          vendor: aiVendor,
          switch_type: aiSwitchType,
          template_id: aiTemplateId,
          validate: true,
          save_to_database: false
        });

        setAiResult(result);
      } else {
        const result = await aiService.generateBatchConfigurations({
          batch_description: aiDescription,
          vendor: aiVendor,
          switch_type: aiSwitchType,
          template_id: aiTemplateId,
          save_to_database: false
        });

        setAiResult(result);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to generate configuration with AI');
    } finally {
      setAiGenerating(false);
    }
  };
  
  const handleTemplateSelect = (template: any) => {
    setSelectedTemplate(template);
    if (configMode === 'ai') {
      setAiTemplateId(template.id);
    }
    setTemplateSelectorOpen(false);
  };
  
  const handleManualSave = async (configData: any) => {
    setLoading(true);
    try {
      const api = await getAuthenticatedApi();
      await api.saveConfiguration({
        switch_name: configData.parameters.switch_name || 'Manual-Config',
        vendor: configData.vendor,
        switch_type: configData.switch_type,
        configuration: configData.configuration,
        template_id: configData.template_id,
        template_name: configData.template_name,
        parsed_parameters: configData.parameters,
        manual: true
      });
      
      setManualDialog(false);
      setSelectedTemplate(null);
      loadConfigurations();
    } catch (error: any) {
      setError(error.message || 'Failed to save manual configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAIConfig = async () => {
    if (!aiResult) return;

    setLoading(true);
    try {
      const api = await getAuthenticatedApi();
      
      if (aiMode === 'single') {
        await api.saveConfiguration({
          switch_name: aiResult.parameters_extracted?.switch_name || 'AI-Generated',
          vendor: aiVendor,
          configuration: aiResult.configuration_text,
          natural_language_request: aiDescription,
          parsed_parameters: aiResult.parameters_extracted
        });
      } else {
        // Save batch configurations
        for (const config of aiResult.configurations) {
          await api.saveConfiguration({
            switch_name: config.switch_name,
            vendor: aiVendor,
            configuration: config.configuration_text,
            natural_language_request: aiDescription
          });
        }
      }

      setAiDialog(false);
      setBatchDialog(false);
      setAiResult(null);
      setAiDescription('');
      loadConfigurations();
    } catch (error: any) {
      setError(error.message || 'Failed to save AI-generated configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleViewConfig = async (config: Configuration) => {
    setSelectedConfig(config);
    
    if (!config.configuration && !config.generated_config && config.id) {
      try {
        const api = await getAuthenticatedApi();
        const fullConfig = await api.getConfiguration(config.id);
        setSelectedConfig({
          ...config,
          configuration: fullConfig.generated_config || fullConfig.configuration || '',
          generated_config: fullConfig.generated_config
        });
      } catch (error) {
        console.error('Failed to fetch configuration:', error);
      }
    }
    
    setViewDialog(true);
  };

  const handleEditConfig = async (config: Configuration) => {
    setSelectedConfig(config);
    const configContent = config.generated_config || config.configuration;
    
    if (!configContent && config.id) {
      try {
        const api = await getAuthenticatedApi();
        const fullConfig = await api.getConfiguration(config.id);
        setEditableContent(fullConfig.generated_config || fullConfig.configuration || '');
      } catch (error) {
        console.error('Failed to fetch configuration:', error);
        setEditableContent('');
      }
    } else {
      setEditableContent(configContent || '');
    }
    
    setEditDialog(true);
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;
    
    setLoading(true);
    try {
      const api = await getAuthenticatedApi();
      await api.deleteConfiguration(configId);
      loadConfigurations();
    } catch (error: any) {
      setError(error.message || 'Failed to delete configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSelected = () => {
    const selected = configurations.filter(c => selectedConfigs.includes(c.id));
    const data = selected.map(c => c.configuration).join('\n\n' + '='.repeat(80) + '\n\n');
    const blob = new Blob([data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `configurations_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  const getValidationIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'invalid':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return null;
    }
  };

  const getConfidenceChip = (score?: number) => {
    if (!score) return null;
    const interpretation = aiService.interpretConfidenceScore(score);
    return (
      <Chip
        size="small"
        label={`${Math.round(score * 100)}%`}
        color={interpretation.color}
        variant="outlined"
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Configuration Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage configurations manually or with AI assistance
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<BuildIcon />}
            onClick={() => {
              setConfigMode('manual');
              setManualDialog(true);
            }}
            color="primary"
          >
            Manual Configuration
          </Button>
          <Button
            variant="contained"
            startIcon={<AIIcon />}
            onClick={() => {
              setConfigMode('ai');
              setAiDialog(true);
            }}
            color="secondary"
          >
            AI Generate
          </Button>
          <Button
            variant="outlined"
            startIcon={<BatchIcon />}
            onClick={() => setBatchDialog(true)}
          >
            Batch Generate
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search configurations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Vendor"
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
            >
              <MenuItem value="all">All Vendors</MenuItem>
              <MenuItem value="extreme">Extreme Networks</MenuItem>
              <MenuItem value="cisco">Cisco</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportSelected}
                disabled={selectedConfigs.length === 0}
              >
                Export Selected ({selectedConfigs.length})
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedConfigs.length > 0 && selectedConfigs.length < filteredConfigs.length}
                  checked={filteredConfigs.length > 0 && selectedConfigs.length === filteredConfigs.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedConfigs(filteredConfigs.map(c => c.id));
                    } else {
                      setSelectedConfigs([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConfigs
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((config) => {
                const isItemSelected = selectedConfigs.includes(config.id);
                return (
                  <TableRow
                    key={config.id}
                    hover
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={() => {
                          const newSelected = isItemSelected
                            ? selectedConfigs.filter(id => id !== config.id)
                            : [...selectedConfigs, config.id];
                          setSelectedConfigs(newSelected);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{config.name}</Typography>
                        {config.ai_generated && (
                          <Tooltip title="AI Generated">
                            <PsychologyIcon fontSize="small" color="primary" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{config.vendor}</TableCell>
                    <TableCell>{config.switch_type || 'N/A'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getValidationIcon(config.validation_status || 'unknown')}
                        <Typography variant="body2">
                          {config.validation_status || 'Not validated'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {config.natural_language_request ? (
                        <Tooltip title={config.natural_language_request}>
                          <Chip 
                            label="NL" 
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Chip 
                          label="Manual" 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                      {getConfidenceChip(config.confidence_score)}
                    </TableCell>
                    <TableCell>
                      {new Date(config.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewConfig(config)}
                          title="View"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditConfig(config)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteConfig(config.id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredConfigs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* AI Generation Dialog */}
      <Dialog open={aiDialog} onClose={() => setAiDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <AIIcon color="primary" />
            <Typography variant="h6">Generate Configuration with AI</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  select
                  fullWidth
                  label="Vendor"
                  value={aiVendor}
                  onChange={(e) => {
                    setAiVendor(e.target.value);
                    setSelectedTemplate(null);
                    setAiTemplateId(null);
                  }}
                >
                  <MenuItem value="extreme">Extreme Networks</MenuItem>
                  <MenuItem value="cisco">Cisco</MenuItem>
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  select
                  fullWidth
                  label="Switch Type"
                  value={aiSwitchType}
                  onChange={(e) => {
                    setAiSwitchType(e.target.value);
                    setSelectedTemplate(null);
                    setAiTemplateId(null);
                  }}
                >
                  <MenuItem value="edge">Edge</MenuItem>
                  <MenuItem value="core">Core</MenuItem>
                  <MenuItem value="distribution">Distribution</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Configuration Template
                    </Typography>
                    {selectedTemplate ? (
                      <Stack spacing={1}>
                        <Typography variant="body2">{selectedTemplate.name}</Typography>
                        {selectedTemplate.description && (
                          <Typography variant="caption" color="text.secondary">
                            {selectedTemplate.description}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No template selected - AI will use default template
                      </Typography>
                    )}
                  </Box>
                  <Button
                    startIcon={<CategoryIcon />}
                    onClick={() => {
                      setConfigMode('ai');
                      setTemplateSelectorOpen(true);
                    }}
                    variant="outlined"
                  >
                    Select Template
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Describe your configuration requirements"
              value={aiDescription}
              onChange={(e) => setAiDescription(e.target.value)}
              placeholder="E.g., Configure edge switch SW-FLOOR2-01 at Building A with IP 192.168.1.10..."
              helperText="Be specific about switch names, IP addresses, VLANs, and features you need"
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Example Descriptions:
              </Typography>
              <Stack spacing={1}>
                {aiExamples.single.map((example, idx) => (
                  <Chip
                    key={idx}
                    label={example}
                    variant="outlined"
                    onClick={() => setAiDescription(example)}
                    sx={{ height: 'auto', py: 1, justifyContent: 'flex-start' }}
                  />
                ))}
              </Stack>
            </Box>

            {aiGenerating && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  AI is generating your configuration...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {aiResult && !aiGenerating && (
              <>
                <Alert severity={aiResult.validation_status === 'valid' ? 'success' : 'warning'}>
                  <Typography variant="subtitle2">
                    Configuration generated successfully!
                  </Typography>
                  <Typography variant="body2">
                    Confidence: {Math.round(aiResult.confidence_score * 100)}%
                  </Typography>
                  {aiResult.validation_errors?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="error">
                        Validation Errors:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {aiResult.validation_errors.map((error: string, idx: number) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </Box>
                  )}
                </Alert>

                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Extracted Parameters:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {Object.entries(aiResult.parameters_extracted || {}).map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${value}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>

                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Editor
                    height="300px"
                    defaultLanguage="shell"
                    value={aiResult.configuration_text}
                    theme="vs-dark"
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      fontSize: 12
                    }}
                  />
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiDialog(false)}>Cancel</Button>
          {!aiResult && (
            <Button
              variant="contained"
              startIcon={<AIIcon />}
              onClick={handleAIGenerate}
              disabled={aiGenerating || !aiDescription}
            >
              Generate
            </Button>
          )}
          {aiResult && (
            <>
              <Button
                onClick={() => {
                  setAiResult(null);
                  setAiDescription('');
                }}
              >
                Generate Another
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveAIConfig}
                disabled={loading}
              >
                Save Configuration
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">View Configuration</Typography>
            {selectedConfig?.ai_generated && (
              <Chip
                icon={<PsychologyIcon />}
                label="AI Generated"
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedConfig?.natural_language_request && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Generated from:</Typography>
              <Typography variant="body2">{selectedConfig.natural_language_request}</Typography>
            </Alert>
          )}
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Editor
              height="400px"
              defaultLanguage="shell"
              value={selectedConfig?.configuration || ''}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 14
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mt: 2 }}>
            <Editor
              height="400px"
              defaultLanguage="shell"
              value={editableContent}
              onChange={(value) => setEditableContent(value || '')}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            // Save logic here
            setEditDialog(false);
          }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Configuration Dialog */}
      <Dialog open={manualDialog && !selectedTemplate} onClose={() => setManualDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <BuildIcon color="primary" />
            <Typography variant="h6">Manual Configuration Setup</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select the vendor and switch type, then choose a template for manual configuration.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  select
                  fullWidth
                  label="Vendor"
                  value={manualVendor}
                  onChange={(e) => setManualVendor(e.target.value)}
                >
                  <MenuItem value="extreme">Extreme Networks</MenuItem>
                  <MenuItem value="cisco">Cisco</MenuItem>
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  select
                  fullWidth
                  label="Switch Type"
                  value={manualSwitchType}
                  onChange={(e) => setManualSwitchType(e.target.value)}
                >
                  <MenuItem value="edge">Edge</MenuItem>
                  <MenuItem value="core">Core</MenuItem>
                  <MenuItem value="distribution">Distribution</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CategoryIcon />}
            onClick={() => {
              setConfigMode('manual');
              setTemplateSelectorOpen(true);
            }}
          >
            Choose Template
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Template Selector */}
      <TemplateSelector
        open={templateSelectorOpen}
        onClose={() => {
          setTemplateSelectorOpen(false);
          if (configMode === 'manual' && !selectedTemplate) {
            setManualDialog(false);
          }
        }}
        onSelect={handleTemplateSelect}
        vendor={configMode === 'manual' ? manualVendor : aiVendor}
        switchType={configMode === 'manual' ? manualSwitchType : aiSwitchType}
        title={configMode === 'manual' ? 'Select Template for Manual Configuration' : 'Select Template for AI Generation'}
      />
      
      {/* Manual Configuration Form */}
      {selectedTemplate && configMode === 'manual' && (
        <ManualConfigurationForm
          open={manualDialog && selectedTemplate !== null}
          onClose={() => {
            setManualDialog(false);
            setSelectedTemplate(null);
          }}
          onSave={handleManualSave}
          template={selectedTemplate}
          vendor={manualVendor}
          switchType={manualSwitchType}
        />
      )}

      {/* Floating Action Button for Quick AI Generation */}
      <Fab
        color="primary"
        aria-label="ai-generate"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setAiDialog(true)}
      >
        <AIIcon />
      </Fab>
    </Box>
  );
};

export default ConfigurationManagementAI;